/**
 * Material editor modal (Spec 016).
 *
 * Two modes:
 *   - create — `material_id` is editable; submit calls `createMaterial`.
 *   - edit   — `material_id` is read-only; submit calls `patchMaterial`
 *              with `If-Match: <version>` optimistic concurrency.
 *
 * Covers every editable Material field, embedding the RecipeBuilder
 * and CatalogPhotoUpload sub-components. A 409 surfaces as a clear
 * "changed elsewhere — reload" message rather than a generic error.
 */

import { useState } from "react";

import { createMaterial, patchMaterial } from "@starz/shared/api/sdk.gen";
import type {
  Material,
  MaterialUnitKind,
  PriceTier,
  PricingMode,
  RecipeComponent,
} from "@starz/shared/api/types.gen";

import { CatalogPhotoUpload } from "./CatalogPhotoUpload";
import { RecipeBuilder } from "./RecipeBuilder";

const UNITS: MaterialUnitKind[] = ["sqft", "linear_ft", "each", "gallon"];
const PRICING_MODES: PricingMode[] = ["itemized", "bundled"];
const PRICE_TIERS: PriceTier[] = ["budget", "mid", "premium", "luxury"];

interface Props {
  /** When set, the editor opens in edit mode for this material. */
  material: Material | null;
  /** All catalog material IDs — for the recipe-component dropdown. */
  materialIds: string[];
  /** All labor task IDs — for the recipe-component dropdown. */
  laborTaskIds: string[];
  onSaved: (material: Material) => void;
  onCancel: () => void;
}

interface FormState {
  material_id: string;
  display_name: string;
  category: string;
  subcategory: string;
  pricing_mode: PricingMode;
  unit: MaterialUnitKind;
  unit_price_dollars: string;
  option_group_id: string;
  option_group_name: string;
  compatible_with: string;
  recipe_components: RecipeComponent[];
  photo_url: string | null;
  description: string;
  price_tier: PriceTier | "";
  style_tags: string;
  room_types: string;
  surface_types: string;
  coverage_sqft_per_unit: string;
  active: boolean;
}

function initialState(m: Material | null): FormState {
  return {
    material_id: m?.material_id ?? "",
    display_name: m?.display_name ?? "",
    category: m?.category ?? "",
    subcategory: m?.subcategory ?? "",
    pricing_mode: m?.pricing_mode ?? "itemized",
    unit: m?.unit ?? "sqft",
    unit_price_dollars: m ? (m.unit_price_cents / 100).toFixed(2) : "0.00",
    option_group_id: m?.option_group_id ?? "",
    option_group_name: m?.option_group_name ?? "",
    compatible_with: (m?.compatible_with ?? []).join(", "),
    recipe_components: m?.recipe_components ?? [],
    photo_url: m?.photo_url ?? null,
    description: m?.description ?? "",
    price_tier: m?.price_tier ?? "",
    style_tags: (m?.style_tags ?? []).join(", "),
    room_types: (m?.room_types ?? []).join(", "),
    surface_types: (m?.surface_types ?? []).join(", "),
    coverage_sqft_per_unit:
      m?.coverage_sqft_per_unit != null ? String(m.coverage_sqft_per_unit) : "",
    active: m?.active ?? true,
  };
}

function csvToList(s: string): string[] {
  return s
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
}

export function MaterialEditor({
  material,
  materialIds,
  laborTaskIds,
  onSaved,
  onCancel,
}: Props) {
  const isEdit = material !== null;
  const [form, setForm] = useState<FormState>(initialState(material));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function onSave() {
    setError(null);

    const priceCents = Math.round(parseFloat(form.unit_price_dollars) * 100);
    if (Number.isNaN(priceCents) || priceCents < 0) {
      setError("Unit price must be a non-negative number.");
      return;
    }
    if (!isEdit && !/^[A-Za-z0-9_-]+$/.test(form.material_id)) {
      setError(
        "Material ID may only contain letters, numbers, hyphens, and underscores.",
      );
      return;
    }
    const coverage =
      form.coverage_sqft_per_unit.trim() === ""
        ? null
        : parseFloat(form.coverage_sqft_per_unit);
    if (coverage !== null && (Number.isNaN(coverage) || coverage <= 0)) {
      setError("Coverage must be a positive number, or left blank.");
      return;
    }

    const shared = {
      display_name: form.display_name.trim(),
      category: form.category.trim(),
      subcategory: form.subcategory.trim() || null,
      pricing_mode: form.pricing_mode,
      unit: form.unit,
      unit_price_cents: priceCents,
      option_group_id: form.option_group_id.trim() || null,
      option_group_name: form.option_group_name.trim() || null,
      compatible_with: csvToList(form.compatible_with),
      recipe_components: form.recipe_components,
      photo_url: form.photo_url,
      description: form.description.trim() || null,
      price_tier: form.price_tier || null,
      style_tags: csvToList(form.style_tags),
      room_types: csvToList(form.room_types),
      surface_types: csvToList(form.surface_types),
      coverage_sqft_per_unit: coverage,
    };

    setSaving(true);
    const result = isEdit
      ? await patchMaterial({
          materialId: material.material_id,
          ifMatchVersion: material.version,
          body: shared,
        })
      : await createMaterial({
          body: { material_id: form.material_id.trim(), active: form.active, ...shared },
        });
    setSaving(false);

    if ("error" in result) {
      const msg = result.error.error.message;
      setError(
        result.error.error.code === "CONFLICT"
          ? isEdit
            ? "This material changed elsewhere — close and reopen to reload."
            : msg
          : msg,
      );
      return;
    }
    onSaved(result.data);
  }

  const inputClass =
    "mt-1 w-full rounded-lg border border-starz-gold/20 bg-white px-3 py-2 text-sm focus:border-starz-gold focus:outline-none focus:ring-2 focus:ring-starz-gold/30";
  const labelClass =
    "block text-xs font-semibold uppercase tracking-widest text-starz-gold";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? "Edit material" : "Add material"}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-starz-charcoal/40 px-4 py-8"
    >
      <div className="w-full max-w-2xl rounded-xl border border-starz-gold/20 bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-serif text-2xl text-starz-navy">
            {isEdit ? "Edit material" : "Add material"}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="text-sm text-warm-gray hover:text-starz-charcoal"
          >
            Cancel
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Material ID</label>
              <input
                type="text"
                value={form.material_id}
                disabled={isEdit}
                onChange={(e) => set("material_id", e.target.value)}
                placeholder="e.g. sw_emerald"
                className={`${inputClass} ${isEdit ? "bg-starz-cream text-warm-gray" : ""}`}
              />
            </div>
            <div>
              <label className={labelClass}>Display name</label>
              <input
                type="text"
                value={form.display_name}
                onChange={(e) => set("display_name", e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Category</label>
              <input
                type="text"
                value={form.category}
                onChange={(e) => set("category", e.target.value)}
                placeholder="e.g. paint"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Subcategory</label>
              <input
                type="text"
                value={form.subcategory}
                onChange={(e) => set("subcategory", e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Pricing mode</label>
              <select
                value={form.pricing_mode}
                onChange={(e) => set("pricing_mode", e.target.value as PricingMode)}
                className={inputClass}
              >
                {PRICING_MODES.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Unit</label>
              <select
                value={form.unit}
                onChange={(e) => set("unit", e.target.value as MaterialUnitKind)}
                className={inputClass}
              >
                {UNITS.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Unit price ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.unit_price_dollars}
                onChange={(e) => set("unit_price_dollars", e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Coverage (sq ft / unit)</label>
              <input
                type="number"
                step="any"
                min="0"
                value={form.coverage_sqft_per_unit}
                onChange={(e) => set("coverage_sqft_per_unit", e.target.value)}
                placeholder="paint only — else blank"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Option group ID</label>
              <input
                type="text"
                value={form.option_group_id}
                onChange={(e) => set("option_group_id", e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Option group name</label>
              <input
                type="text"
                value={form.option_group_name}
                onChange={(e) => set("option_group_name", e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Price tier</label>
              <select
                value={form.price_tier}
                onChange={(e) => set("price_tier", e.target.value as PriceTier | "")}
                className={inputClass}
              >
                <option value="">— none —</option>
                {PRICE_TIERS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm text-starz-charcoal">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => set("active", e.target.checked)}
                  className="h-4 w-4"
                />
                Active
              </label>
            </div>
          </div>

          <div>
            <label className={labelClass}>Description</label>
            <textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              rows={2}
              className={inputClass}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className={labelClass}>Style tags</label>
              <input
                type="text"
                value={form.style_tags}
                onChange={(e) => set("style_tags", e.target.value)}
                placeholder="comma, separated"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Room types</label>
              <input
                type="text"
                value={form.room_types}
                onChange={(e) => set("room_types", e.target.value)}
                placeholder="comma, separated"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Surface types</label>
              <input
                type="text"
                value={form.surface_types}
                onChange={(e) => set("surface_types", e.target.value)}
                placeholder="comma, separated"
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Compatible-with groups</label>
            <input
              type="text"
              value={form.compatible_with}
              onChange={(e) => set("compatible_with", e.target.value)}
              placeholder="option_group_ids, comma separated"
              className={inputClass}
            />
          </div>

          <CatalogPhotoUpload
            materialId={form.material_id}
            value={form.photo_url}
            onChange={(url) => set("photo_url", url)}
          />

          <RecipeBuilder
            components={form.recipe_components}
            materialIds={materialIds}
            laborTaskIds={laborTaskIds}
            onChange={(components) => set("recipe_components", components)}
          />

          {error ? (
            <p className="border-l-2 border-starz-brick pl-3 text-sm text-starz-brick">
              {error}
            </p>
          ) : null}

          <div className="flex justify-end gap-3 border-t border-starz-gold/15 pt-4">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="rounded-md border border-starz-gold/30 bg-white px-4 py-2 text-sm font-medium text-starz-charcoal hover:bg-starz-cream"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void onSave()}
              disabled={saving}
              className="rounded-md bg-starz-navy px-5 py-2 text-sm font-semibold text-starz-off-white transition-opacity hover:opacity-90 disabled:opacity-40"
            >
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create material"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
